import { useContext, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import Button from '@atoms/Button'
import Modal from '@molecules/Modal'
import TextInput from '@molecules/TextInput'
import { changeCognitoMail, resendConfirmMail } from '@models/User'
import { useTranslation } from '@models/Translation'
import { sendError } from '@models/Sentry'
import { StoreContext } from '@models/Store'

import * as style from './style.module.sass'

type MailConfirmFormData = {
  code: string
}

type MailConfirmModalProps = {
  show: boolean
  mail: string
  newMail?: string
  password?: string
  onSubmit: (code: string) => Promise<void>
}
const MailConfirmModal = ({
  show,
  mail,
  newMail,
  password = '',
  onSubmit,
}: MailConfirmModalProps) => {
  const { action } = useContext(StoreContext)
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)

  const methods = useForm<MailConfirmFormData>()
  const { handleSubmit, formState } = methods
  const { errors } = formState
  const submit = async (data: MailConfirmFormData) => {
    setLoading(true)
    await onSubmit(data.code)
    setLoading(false)
  }

  const resent = async () => {
    setResending(true)
    try {
      if (newMail === undefined) {
        await resendConfirmMail(mail)
      } else {
        await changeCognitoMail(mail, password, newMail)
      }
    } catch (error) {
      action.addToasts(t('mypage.failRequest'))
      sendError(error)
    }
    setResending(false)
  }

  return (
    <Modal type="overlay" show={show}>
      <FormProvider {...methods}>
        <form className={style.confirm} onSubmit={handleSubmit(submit)}>
          <div className={style.title}>{t('mypage.inputCode')}</div>
          <div className={style.mail}>{newMail || mail}</div>
          <div className={style.text}>{t('mypage.sendCode')}</div>

          <TextInput
            name="code"
            label={t('mypage.code')}
            registerOptions={{ required: true, maxLength: 6, minLength: 6 }}
            errorMessage={
              errors.code !== undefined ? t('mypage.invalidCode') : undefined
            }
            className={style.input}
            design="code"
          />

          <div className={style.resent}>
            {t('mypage.noCode')}
            <button
              type="button"
              onClick={resent}
              data-loading={resending}
              disabled={resending}
            >
              {t('mypage.noCodeResend')}
            </button>
          </div>
          <div className={style.buttonContainer}>
            <Button type="submit" loading={loading}>
              {t('mypage.verify')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  )
}

export default MailConfirmModal
